<template>
  <v-row>
    <v-col cols="12" md="10" :class="{ 'bg-red-lighten-4': !release.release }">
      {{ castCrew.first_name }} {{ castCrew.last_name }}
    </v-col>
    <v-col cols="12" md="2" class="d-flex justify-end">
      <v-btn icon variant="text" small color="primary" @click="isSignatureOpen = !isSignatureOpen">
        <v-icon icon="mdi-draw-pen"></v-icon>
      </v-btn>
      <v-btn icon variant="text" small color="primary" @click="isUploadOpen = !isUploadOpen">
        <v-icon icon="mdi-upload"></v-icon>
      </v-btn>
      <v-btn icon variant="text" small color="primary" @click="sendEmailModal = !sendEmailModal">
        <v-icon icon="mdi-email"></v-icon>
      </v-btn>

      <v-btn
        :disabled="!release.release"
        icon
        variant="text"
        small
        color="primary"
        @click="() => downloadFile(release.release?.url)">
        <v-icon icon="mdi-download"></v-icon>
      </v-btn>
    </v-col>
  </v-row>
  <v-dialog v-model="isUploadOpen">
    <AssetUploadComponent
      :type="['image', 'pdf']"
      :on-upload="handleFileUpload"
      @upload-canceled="isUploadOpen = false"
      @upload-completed="handleUploadComplted"></AssetUploadComponent>
  </v-dialog>
  <v-dialog v-model="isSignatureOpen">
    <ReleaseSignComponent
      :form="talenForm"
      :formFields="fields"
      @sig-completed="handleSigUpload"></ReleaseSignComponent>
  </v-dialog>
  <v-dialog v-model="sendEmailModal">
    <ReleaseSendEmailComponent
      :submission-id="submissionId"
      :castCrew="castCrew"
      @close="sendEmailModal = false"></ReleaseSendEmailComponent>
  </v-dialog>
</template>
<script setup lang="ts">
import { createProgressHandler, uploadSubmissionRelease } from '@/controllers';
import {
  IAssetDTO,
  IReleaseDTO,
  IReleaseTypeDTO,
  ISubmissionCastCrewDTO,
  ISubmissionDTO,
  ReleaseDTO,
  SubmissionCastCrewDTO,
} from '@/dtos';
import { computed, onMounted, ref, watch } from 'vue';
import ReleaseSignComponent from './ReleaseSignComponent.vue';
import AssetUploadComponent from '../ui/AssetUploadComponent.vue';
import { browser, currentDate, downloadFile, IBaseFormFields, userIP } from '@/helpers';
import { DEFAULT_TALENT_FORM, useFormTemplateStore, useTeamStore } from '@/stores';
import { useCastCrewStore } from '@/stores/modules/castcrew';
import ReleaseSendEmailComponent from './ReleaseSendEmailComponent.vue';

interface Props {
  castCrew: ISubmissionCastCrewDTO;
  releaseType: IReleaseTypeDTO;
  submission: ISubmissionDTO;
}
const props = defineProps<Props>();
const castCrew = ref<ISubmissionCastCrewDTO>(new SubmissionCastCrewDTO());
const release = ref<IReleaseDTO>(new ReleaseDTO());
const isUploadOpen = ref<boolean>(false);
const isSignatureOpen = ref<boolean>(false);
const sendEmailModal = ref<boolean>(false);
const formStore = useFormTemplateStore();
const teamStore = useTeamStore();
const castCrewStore = useCastCrewStore();
const submissionId = computed((): string => props.submission.id);

const fields = ref<IBaseFormFields>({
  date: currentDate.value,
  ipAddress: '',
  browser: browser.value,
  teamName: '',
});

const { data: talenForm } = formStore.getFormTemplateById(DEFAULT_TALENT_FORM);

watch(
  () => props.castCrew,
  (newValue?: ISubmissionCastCrewDTO) => {
    if (newValue) {
      castCrew.value = new SubmissionCastCrewDTO(newValue);
    } else {
      castCrew.value = new SubmissionCastCrewDTO();
    }
  },
  { immediate: true },
);

watch(
  () => props.castCrew,
  (newValue?: SubmissionCastCrewDTO) => {
    if (newValue) {
      release.value = new ReleaseDTO(newValue.release);
      release.value.title = `${castCrew.value.first_name} ${castCrew.value.last_name}`;
    } else {
      release.value = new ReleaseDTO();
    }
  },
  { immediate: true },
);

const handleFileUpload = async (
  file: File,
  updateProgress: (progress: number) => void,
): Promise<IAssetDTO> => {
  const response = await uploadSubmissionRelease(
    props.submission.id,
    props.releaseType.id,
    file,
    updateProgress,
  );
  release.value.release = response;
  return response;
};

const handleUploadComplted = async (): Promise<void> => {
  release.value.submission = props.submission;
  release.value.releaseType = props.releaseType;
  castCrew.value.release = release.value;
  await castCrewStore.saveCast.mutateAsync(castCrew.value);
  isUploadOpen.value = false;
};

const handleSigUpload = async (file: File) => {
  const uploadProgress = ref<number>(0);
  const uploadCompleted = ref<boolean>(false);
  const progress = createProgressHandler(uploadProgress, uploadCompleted);
  await handleFileUpload(file, progress);
  await handleUploadComplted();
  isSignatureOpen.value = false;
};

onMounted(async () => {
  fields.value.ipAddress = (await userIP.value) || '';
  fields.value.teamName = teamStore.myTeam?.name;
});
</script>
